import React, { useCallback, useState } from "react";
import { Facet } from "@linkfluence/models/facet";
import { useDebouncedEffect } from "@lkf/hooks";
import Checkbox from "@ui-kit/form/checkbox";
import Dropdown from "@radarly/components/panels/card-edition/sidebar/dropdown";
import Line from "@radarly/components/panels/card-edition/sidebar/line";
import { useFeature } from "@radarly/core/features/use-cases";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { tr } from "@radarly/i18n";
import { MultipleLineChartCard } from "@radarly/models/cards/model";
import { getTermFromCard } from "@radarly/models/cards/term";
import {
  DatavizMultipleLineChart,
  updateDatavizOptions,
} from "@radarly/models/dataviz";
import TimeUnitOption from "@radarly/shared/panels/card-edition/sidebar/shared/time-unit";
import { PanelType } from "../..";
import FilteredValue from "./filtered-value";
import { useSort } from "./sort";

export interface SharedPanelsCardEditionSidebarOptionsMultipleLineChartProps {
  setDataviz: (dataviz: DatavizMultipleLineChart) => void;
  card: MultipleLineChartCard;
  setCard: (card: MultipleLineChartCard) => void;
  setPanel: (panel: { type: PanelType; index?: number }) => void;
}

const getFilteredValue = (
  tag: { value: string; color: string },
  term: Facet
) => {
  if (tag) {
    if (term === Facet.Hashtags) {
      if (!tag.value.startsWith("#")) {
        return tag.value.trim().length === 0
          ? { value: "", color: tag.color }
          : { value: `#${tag.value}`, color: tag.color };
      } else {
        return tag.value.trim().length === 1
          ? { value: "", color: tag.color }
          : tag;
      }
    }
    return tag;
  }
  return null;
};

export default function SharedPanelsCardEditionSidebarOptionsMultipleLineChart({
  setDataviz,
  card,
  setCard,
  setPanel,
}: SharedPanelsCardEditionSidebarOptionsMultipleLineChartProps) {
  const enabledMultiLineLegendSort = useFeature(
    "discovery_multi_line_legend_sort"
  );
  const locale = useCurrentUserLocale();
  const term = getTermFromCard(card);
  const tradKey = term === Facet.Hashtags ? "HASHTAG" : "KEYPHRASE";
  const dataviz: DatavizMultipleLineChart = card.dataviz;
  const facet = getTermFromCard(card);
  const [filteredValues, setFilteredValues] = useState<
    DatavizMultipleLineChart["filteredValues"]
  >(dataviz.filteredValues ? [...dataviz.filteredValues] : []);
  const hasFilteredValues =
    facet === Facet.Keyphrases || facet === Facet.Hashtags;

  const updateDisplayVolumes = useCallback(() => {
    const value = dataviz.displayVolumes ? false : true;
    setDataviz(
      updateDatavizOptions(
        dataviz,
        "displayVolumes",
        value as DatavizMultipleLineChart["displayVolumes"]
      )
    );
  }, [dataviz, setDataviz]);

  const { label, options, onChange, option } = useSort(dataviz, setDataviz);

  const onChangeFilteredValue = (
    index: number,
    value: string = "",
    color: string = ""
  ) => {
    const newFilteredValues = [...filteredValues];
    newFilteredValues[index] = { value, color };
    setFilteredValues(newFilteredValues);
  };

  useDebouncedEffect(
    () => {
      setDataviz(
        updateDatavizOptions(dataviz, "filteredValues", filteredValues)
      );
    },
    1000,
    [filteredValues]
  );

  return (
    <>
      {hasFilteredValues && (
        <Line title="Displayed items">
          <FilteredValue
            filteredValue={getFilteredValue(filteredValues[0], term as Facet)}
            onClickColor={() => setPanel({ type: PanelType.Color, index: 0 })}
            onChangeFilteredValue={onChangeFilteredValue}
            placeholder={tr(locale, `${tradKey}_LABEL`, { value: 1 })}
            index={0}
          />
          <FilteredValue
            filteredValue={getFilteredValue(filteredValues[1], term as Facet)}
            onClickColor={() => setPanel({ type: PanelType.Color, index: 1 })}
            onChangeFilteredValue={onChangeFilteredValue}
            placeholder={tr(locale, `${tradKey}_LABEL`, { value: 2 })}
            index={1}
          />
          <FilteredValue
            filteredValue={getFilteredValue(filteredValues[2], term as Facet)}
            onClickColor={() => setPanel({ type: PanelType.Color, index: 2 })}
            onChangeFilteredValue={onChangeFilteredValue}
            placeholder={tr(locale, `${tradKey}_LABEL`, { value: 3 })}
            index={2}
          />
        </Line>
      )}
      <Line title={tr(locale, "CUSTOM_PAGES::EDITOR::CARD_LEGEND")}>
        <Checkbox
          label={tr(locale, "CUSTOM_PAGES::EDITOR::DATAVIZ::DISPLAY_VOLUMES")}
          checked={dataviz.displayVolumes || undefined}
          onClick={updateDisplayVolumes}
        />
      </Line>
      {enabledMultiLineLegendSort && (
        <Dropdown
          label={label}
          value={option}
          options={options}
          onChange={onChange}
        />
      )}
      <TimeUnitOption dataviz={dataviz} card={card} setCard={setCard} />
    </>
  );
}
